import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { AdminLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Trash2, FileUp, Phone, Upload } from "lucide-react";
import CustomerProfileSheet from "@/components/CustomerProfileSheet";

const CustomerBases = () => {
    const { token } = useAuth();
    const [bases, setBases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [form, setForm] = useState({ name: "", description: "", raw_numbers: "" });
    const [profilePhone, setProfilePhone] = useState(null);

    // CSV upload state
    const [showUpload, setShowUpload] = useState(false);
    const [uploadName, setUploadName] = useState("");
    const [uploadDesc, setUploadDesc] = useState("");
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    const handleUpload = async () => {
        if (!uploadName.trim()) { toast.error("Name required"); return; }
        if (!uploadFile) { toast.error("Pick a CSV file"); return; }
        const fd = new FormData();
        fd.append("name", uploadName.trim());
        fd.append("description", uploadDesc.trim());
        fd.append("file", uploadFile);
        setUploading(true);
        try {
            const { data } = await axios.post(`${API}/customer-bases/upload-csv`, fd, {
                headers: { ...authH(), "Content-Type": "multipart/form-data" },
            });
            toast.success(`Imported "${data.name}" — ${data.count} numbers`);
            setShowUpload(false);
            setUploadName(""); setUploadDesc(""); setUploadFile(null);
            fetchBases();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed");
        } finally { setUploading(false); }
    };

    const authH = () => ({ Authorization: `Bearer ${token}` });

    const fetchBases = async () => {
        try {
            const { data } = await axios.get(`${API}/customer-bases`, { headers: authH() });
            setBases(data || []);
        } catch {
            toast.error("Failed to load customer bases");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchBases(); }, []);

    const parseNumbers = (text) => {
        if (!text) return [];
        return text
            .split(/[\s,;\n\r]+/)
            .map((n) => n.trim())
            .filter(Boolean);
    };

    const handleSave = async () => {
        const nums = parseNumbers(form.raw_numbers);
        if (!form.name || nums.length === 0) {
            toast.error("Name and at least one phone number required");
            return;
        }
        try {
            await axios.post(
                `${API}/customer-bases`,
                { name: form.name, description: form.description, phone_numbers: nums },
                { headers: authH() }
            );
            toast.success(`Created with ${nums.length} numbers`);
            setShowDialog(false);
            setForm({ name: "", description: "", raw_numbers: "" });
            fetchBases();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const handleCSV = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const text = await file.text();
        const numbers = parseNumbers(text);
        setForm((p) => ({
            ...p,
            raw_numbers: [...new Set([...parseNumbers(p.raw_numbers), ...numbers])].join("\n"),
        }));
        toast.success(`Parsed ${numbers.length} numbers from CSV`);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this customer base? Campaigns using it will keep their recipient count but won't dispatch.")) return;
        try {
            await axios.delete(`${API}/customer-bases/${id}`, { headers: authH() });
            toast.success("Deleted");
            fetchBases();
        } catch { toast.error("Failed"); }
    };

    return (
        <AdminLayout title="Customer Bases">
            <div className="space-y-6" data-testid="customer-bases-page">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <Users className="h-6 w-6 text-electric" />
                        <div>
                            <h1 className="text-2xl font-heading font-bold text-white">Customer Bases</h1>
                            <p className="text-white/50 text-sm">
                                Reusable lists of subscriber phone numbers for SMS campaigns. Paste manually or upload CSV.
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={() => setShowUpload(true)} variant="outline" className="border-border text-white hover:bg-subtle" data-testid="upload-csv-btn">
                            <Upload className="h-4 w-4 mr-2" /> Upload CSV
                        </Button>
                        <Button onClick={() => setShowDialog(true)} className="bg-electric hover:bg-vodaredDark text-white" data-testid="new-base-btn">
                            <Plus className="h-4 w-4 mr-2" /> New Base
                        </Button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-12 text-white/40">Loading...</div>
                ) : bases.length === 0 ? (
                    <Card className="bg-paper border-border p-12 text-center">
                        <Users className="h-12 w-12 text-white/20 mx-auto mb-3" />
                        <p className="text-white/60">No customer bases yet.</p>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {bases.map((b, i) => (
                            <Card key={b.id} className="bg-paper border-border p-4" data-testid={`base-card-${i}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <Phone className="h-4 w-4 text-electric" />
                                        <h3 className="font-heading font-bold text-white">{b.name}</h3>
                                    </div>
                                    <Badge className="bg-electric/15 text-electric border-electric/30">
                                        {b.count} numbers
                                    </Badge>
                                </div>
                                {b.description && (
                                    <p className="text-white/60 text-sm mb-3">{b.description}</p>
                                )}
                                <div className="text-white/40 text-xs mb-3 flex flex-wrap gap-x-2 gap-y-1">
                                    {b.phone_numbers.slice(0, 6).map((p) => (
                                        <button
                                            key={p}
                                            onClick={() => setProfilePhone(p)}
                                            className="font-mono hover:text-electric underline-offset-2 hover:underline transition-colors"
                                            data-testid={`open-profile-${p}`}
                                            title="View customer profile"
                                        >
                                            {p}
                                        </button>
                                    ))}
                                    {b.phone_numbers.length > 6 && (
                                        <span className="text-white/30">+{b.phone_numbers.length - 6} more</span>
                                    )}
                                </div>
                                <div className="flex justify-end">
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDelete(b.id)}
                                        className="text-white/60 hover:text-red-400"
                                        data-testid={`delete-base-${i}`}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="bg-paper border-border text-white max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-white font-heading">New Customer Base</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="text-white/70">Name</Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="bg-subtle border-border text-white"
                                placeholder="e.g. Dar es Salaam Football Fans"
                                data-testid="base-name-input"
                            />
                        </div>
                        <div>
                            <Label className="text-white/70">Description (optional)</Label>
                            <Input
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                className="bg-subtle border-border text-white"
                            />
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <Label className="text-white/70">Phone Numbers</Label>
                                <label className="inline-flex items-center gap-1 text-electric text-xs hover:underline cursor-pointer">
                                    <FileUp className="h-3 w-3" /> Upload CSV
                                    <input type="file" accept=".csv,.txt" onChange={handleCSV} className="hidden" data-testid="csv-upload" />
                                </label>
                            </div>
                            <Textarea
                                value={form.raw_numbers}
                                onChange={(e) => setForm({ ...form, raw_numbers: e.target.value })}
                                className="bg-subtle border-border text-white h-40 font-mono text-sm"
                                placeholder={"+255712000001\n+255712000002\n+255712000003"}
                                data-testid="base-numbers-input"
                            />
                            <p className="text-white/40 text-xs mt-1">
                                {parseNumbers(form.raw_numbers).length} numbers parsed. Supports comma, space, or newline separation.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setShowDialog(false)} className="text-white/60">Cancel</Button>
                        <Button onClick={handleSave} className="bg-electric hover:bg-vodaredDark text-white" data-testid="save-base-btn">
                            Save Base
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={showUpload} onOpenChange={setShowUpload}>
                <DialogContent className="bg-paper border-border text-white max-w-xl" data-testid="upload-csv-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white font-heading flex items-center gap-2">
                            <Upload className="h-5 w-5 text-electric" /> Upload CSV
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="text-white/70">Base name</Label>
                            <Input
                                value={uploadName}
                                onChange={(e) => setUploadName(e.target.value)}
                                placeholder='e.g. "Promo Mar 2026"'
                                className="bg-subtle border-border text-white"
                                data-testid="upload-name-input"
                            />
                        </div>
                        <div>
                            <Label className="text-white/70">Description (optional)</Label>
                            <Input
                                value={uploadDesc}
                                onChange={(e) => setUploadDesc(e.target.value)}
                                placeholder="Where these numbers came from"
                                className="bg-subtle border-border text-white"
                                data-testid="upload-desc-input"
                            />
                        </div>
                        <div>
                            <Label className="text-white/70">Region tag (optional)</Label>
                            <Input
                                value={uploadRegion}
                                onChange={(e) => setUploadRegion(e.target.value)}
                                placeholder="e.g. Mwanza, Arusha, Mbeya"
                                className="bg-subtle border-border text-white"
                                data-testid="upload-region-input"
                            />
                            <p className="text-white/40 text-[10px] mt-1">
                                Applied to every phone unless the CSV has a <span className="text-electric">region</span> / <span className="text-electric">city</span> column (which wins per row).
                                Powers regional analytics over time.
                            </p>
                        </div>
                        <div>
                            <Label className="text-white/70">CSV file</Label>
                            <Input
                                type="file"
                                accept=".csv,.txt,.tsv"
                                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                                className="bg-subtle border-border text-white file:text-white file:bg-electric file:border-0 file:px-3 file:py-1 file:rounded-sm file:mr-3"
                                data-testid="upload-file-input"
                            />
                            <p className="text-white/40 text-xs mt-2 leading-relaxed">
                                Accepts comma / semicolon / tab-separated files. A header row is optional —
                                if present we look for a column named <span className="text-electric">phone</span>, <span className="text-electric">msisdn</span>, <span className="text-electric">mobile</span>, etc.
                                Otherwise every cell is scanned for anything that looks like a phone number (9+ digits, optional leading <span className="font-mono">+</span>).
                                Whitespace and dashes are stripped, duplicates removed.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setShowUpload(false)} className="text-white/60">Cancel</Button>
                        <Button
                            onClick={handleUpload}
                            disabled={uploading}
                            className="bg-electric hover:bg-vodaredDark text-white"
                            data-testid="upload-submit-btn"
                        >
                            <Upload className="h-4 w-4 mr-2" />
                            {uploading ? "Uploading…" : "Upload & create base"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <CustomerProfileSheet
                phone={profilePhone}
                open={!!profilePhone}
                onOpenChange={(o) => { if (!o) setProfilePhone(null); }}
            />
        </AdminLayout>
    );
};

export default CustomerBases;
